import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Clock, CheckCircle2, XCircle, AlertCircle, IdCard, Camera, Trash2, Loader2, ExternalLink, UserPlus, FileText, Eye, ShieldCheck, PlayCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CorporateRequestCard from "@/components/corporate/CorporateRequestCard";
import KycDocsSection from "@/components/KycDocUploader";
import { useTour } from "@/components/ProductTour";

// Keyed by derived `kyc_state` from the backend (compute_kyc_state).
const KYC_META = {
  new_registration: { color: "bg-stone-100 text-stone-800 border-stone-200", icon: UserPlus, title: "Welcome aboard — let's complete your KYC", desc: "Fill in your PAN, Aadhaar, and bank details below and upload the supporting documents to start the verification process." },
  documents_pending: { color: "bg-amber-50 text-amber-800 border-amber-200", icon: FileText, title: "Documents pending", desc: "You've started — please complete every typed field AND upload all four KYC documents so the operations team can verify and approve your profile." },
  under_review: { color: "bg-blue-50 text-blue-800 border-blue-200", icon: Eye, title: "Under review", desc: "All your documents are in. Our operations team is verifying your profile (usually within 24h). You'll see PAN / Bank / Mobile tick off one by one below." },
  approved: { color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, title: "Profile approved", desc: "Your profile is approved. Operations may still be ticking off the final verification checks." },
  verified: { color: "bg-emerald-50 text-emerald-900 border-emerald-300", icon: ShieldCheck, title: "Verified ✓", desc: "All three checks (PAN, Bank, Mobile) are complete. You can request NEFT withdrawals from your wallet." },
  rejected: { color: "bg-red-50 text-red-800 border-red-200", icon: XCircle, title: "Profile needs an update", desc: "Fix the issues mentioned below and re-upload — your profile will move back to Under Review automatically." },
};

const CHECK_ITEMS = [
  { key: "kyc_pan_verified", label: "PAN" },
  { key: "kyc_bank_verified", label: "Bank account" },
  { key: "kyc_mobile_verified", label: "Mobile" },
];

/** Client-side resize → JPEG to keep base64 lean before upload. */
async function resizeToJpegBase64(file, maxDim = 512, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { refresh } = useAuth();
  const tour = useTour();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [specialtyDraft, setSpecialtyDraft] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    const r = await api.get("/auth/me");
    setForm(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, pan: form.pan, aadhaar: form.aadhaar,
        bank_account: form.bank_account, ifsc: form.ifsc, account_holder: form.account_holder,
        city: form.city, bio: form.bio, years_experience: form.years_experience ? Number(form.years_experience) : null,
        specialties: form.specialties || [], whatsapp_number: form.whatsapp_number || null,
        gstin: form.gstin !== undefined ? (form.gstin || "").trim() : null,
      };
      await api.put("/profile", payload);
      toast.success("Profile updated");
      refresh();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const onPickPhoto = () => fileRef.current?.click();

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Please upload a JPG, PNG, or WebP image");
      return;
    }
    setUploadingPhoto(true);
    try {
      const dataUri = await resizeToJpegBase64(file, 512, 0.82);
      await api.post("/profile/photo", { photo_base64: dataUri });
      toast.success("Photo updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload photo");
    } finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    if (!form.photo_base64) return;
    if (!window.confirm("Remove your profile photo?")) return;
    try {
      await api.delete("/profile/photo");
      toast.success("Photo removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const addSpecialty = () => {
    const v = (specialtyDraft || "").trim();
    if (!v) return;
    const cur = form.specialties || [];
    if (cur.length >= 6) { toast.info("Max 6 specialties"); return; }
    if (cur.includes(v)) return;
    setForm({ ...form, specialties: [...cur, v] });
    setSpecialtyDraft("");
  };
  const removeSpecialty = (s) => setForm({ ...form, specialties: (form.specialties || []).filter((x) => x !== s) });

  const kycState = form.kyc_state || "new_registration";
  const meta = KYC_META[kycState] || KYC_META.new_registration;
  const Icon = meta.icon;
  const profileLocked = form.profile_status === "Approved";

  const landingUrl = form.referral_code ? `${window.location.origin}/r/${form.referral_code}` : "";

  return (
    <div className="space-y-8" data-testid="profile-page">
      <CorporateRequestCard />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Account</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">Profile & KYC</h1>
        </div>
        <Link to="/card" data-testid="profile-view-card">
          <Button variant="outline" className="rounded-full border-stone-300 h-11">
            <IdCard className="w-4 h-4 mr-2" /> Digital Card
          </Button>
        </Link>
        <Button onClick={tour.start} variant="outline" data-testid="profile-replay-tour"
          className="rounded-full border-stone-300 h-11">
          <PlayCircle className="w-4 h-4 mr-2" /> Take the tour again
        </Button>
      </div>

      <div className={`rounded-2xl border-2 p-5 ${meta.color}`} data-testid="profile-status-banner">
        <div className="flex gap-4">
          <Icon className="w-6 h-6 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-black text-lg" data-testid="profile-kyc-state-title">{meta.title}</div>
            <p className="text-sm mt-0.5 opacity-90">{meta.desc}</p>
            {kycState === "rejected" && form.profile_rejection_reason && (
              <p className="text-sm mt-2 font-mono bg-white/50 rounded px-3 py-2" data-testid="profile-rejection-reason">Reason: {form.profile_rejection_reason}</p>
            )}
          </div>
        </div>
        {(kycState === "under_review" || kycState === "approved" || kycState === "verified") && (
          <div className="mt-4 pt-4 border-t border-current/20 flex flex-wrap gap-2" data-testid="profile-verify-checks">
            {CHECK_ITEMS.map(({ key, label }) => {
              const done = !!form[key];
              return (
                <span key={key} data-testid={`profile-check-${key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${
                    done ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-white/60 text-stone-700 border-stone-300"
                  }`}>
                  {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  {label} {done ? "verified" : "pending"}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Photo + Public Landing block */}
      <div className="bg-white rounded-2xl border border-stone-200 p-7 max-w-3xl" data-testid="profile-photo-block">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="relative">
            <div className="w-28 h-28 rounded-2xl bg-stone-100 border-2 border-stone-200 overflow-hidden grid place-items-center">
              {form.photo_base64 ? (
                <img src={form.photo_base64} alt="profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
              ) : (
                <Camera className="w-9 h-9 text-stone-400" />
              )}
            </div>
            {uploadingPhoto && <div className="absolute inset-0 bg-white/70 grid place-items-center rounded-2xl"><Loader2 className="w-6 h-6 animate-spin text-emerald-900" /></div>}
          </div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-display text-lg font-black">Your public photo</h3>
            <p className="text-sm text-stone-500 mt-1">Shown on your referral landing page <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">/r/{form.referral_code || "…"}</code>. JPG/PNG/WebP, auto-resized to 512px.</p>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhotoChange} data-testid="profile-photo-input" />
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button type="button" onClick={onPickPhoto} disabled={uploadingPhoto} data-testid="profile-photo-upload-btn"
                className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold h-10">
                <Camera className="w-4 h-4 mr-2" /> {form.photo_base64 ? "Replace" : "Upload"} photo
              </Button>
              {form.photo_base64 && (
                <Button type="button" variant="outline" onClick={removePhoto} data-testid="profile-photo-remove-btn"
                  className="rounded-full border-stone-300 h-10 text-red-700">
                  <Trash2 className="w-4 h-4 mr-2" /> Remove
                </Button>
              )}
              {landingUrl && (
                <a href={landingUrl} target="_blank" rel="noreferrer" data-testid="profile-landing-link">
                  <Button type="button" variant="outline" className="rounded-full border-stone-300 h-10">
                    <ExternalLink className="w-4 h-4 mr-2" /> View landing
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={save} className="bg-white rounded-2xl border border-stone-200 p-7 space-y-6 max-w-3xl">
        <div className="grid sm:grid-cols-2 gap-5">
          <div><Label className="font-semibold">Full Name</Label><Input data-testid="profile-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-lg" /></div>
          <div><Label className="font-semibold">Mobile</Label><Input value={`+91 ${form.mobile || ""}`} disabled className="mt-1.5 h-11 rounded-lg bg-stone-100" /></div>
          <div><Label className="font-semibold">Email</Label><Input data-testid="profile-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-lg" /></div>
          <div><Label className="font-semibold">City</Label><Input data-testid="profile-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-11 rounded-lg" /></div>
          <div className="sm:col-span-2">
            <Label className="font-semibold">GSTIN <span className="font-normal text-stone-400">(optional — for GST-registered advisors)</span></Label>
            <Input data-testid="profile-gstin" value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="e.g. 07ABCDE1234F1Z5" className="mt-1.5 h-11 rounded-lg uppercase" />
            <p className="text-[11px] text-stone-500 mt-1">If set, a GST commission invoice (you → Crece) is auto-generated every time a payout is transferred to you.</p>
          </div>
        </div>

        {/* Public landing fields */}
        <div className="pt-4 border-t border-stone-200">
          <h3 className="font-display text-lg font-black mb-1">Public landing</h3>
          <p className="text-xs text-stone-500 mb-4">Shown publicly on <code className="bg-stone-100 px-1 py-0.5 rounded">/r/{form.referral_code || "…"}</code>. Helps prospects trust you before signing up.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="font-semibold">Years of experience</Label>
              <Input data-testid="profile-years-exp" type="number" min={0} max={60} value={form.years_experience ?? ""} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className="mt-1.5 h-11 rounded-lg" placeholder="e.g. 7" />
            </div>
            <div>
              <Label className="font-semibold">WhatsApp number <span className="font-normal text-stone-400">(if different from mobile)</span></Label>
              <Input data-testid="profile-whatsapp" value={form.whatsapp_number || ""} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="mt-1.5 h-11 rounded-lg" placeholder="9876543210" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-semibold">Short bio <span className="font-normal text-stone-400">(max 240 chars)</span></Label>
              <Textarea data-testid="profile-bio" value={form.bio || ""} maxLength={240} rows={3} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5 rounded-lg" placeholder="I help families pick the right insurance and credit products — backed by 7 years of banking experience." />
              <div className="text-xs text-stone-400 mt-1 text-right">{(form.bio || "").length}/240</div>
            </div>
            <div className="sm:col-span-2">
              <Label className="font-semibold">Specialties <span className="font-normal text-stone-400">(up to 6)</span></Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="profile-specialty-input" value={specialtyDraft} onChange={(e) => setSpecialtyDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSpecialty(); } }}
                  className="h-11 rounded-lg" placeholder="e.g. Health Insurance" />
                <Button type="button" data-testid="profile-specialty-add" onClick={addSpecialty} variant="outline" className="rounded-full h-11 border-stone-300">Add</Button>
              </div>
              {(form.specialties || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-specialty-list">
                  {form.specialties.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-900 border border-emerald-200 px-3 py-1.5 rounded-full text-xs font-semibold">
                      {s}
                      <button type="button" onClick={() => removeSpecialty(s)} className="hover:text-red-700"><XCircle className="w-3.5 h-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-stone-200">
          <h3 className="font-display text-lg font-black mb-4">KYC Documents</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="font-semibold">PAN Number</Label><Input data-testid="profile-pan" value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="mt-1.5 h-11 rounded-lg uppercase" /></div>
            <div><Label className="font-semibold">Aadhaar (last 4)</Label><Input data-testid="profile-aadhaar" value={form.aadhaar || ""} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} className="mt-1.5 h-11 rounded-lg" /></div>
          </div>
        </div>

        <div className="pt-4 border-t border-stone-200">
          <h3 className="font-display text-lg font-black mb-4">Bank Details (for NEFT)</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="font-semibold">Account Holder</Label><Input data-testid="profile-account-holder" value={form.account_holder || ""} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} className="mt-1.5 h-11 rounded-lg" /></div>
            <div><Label className="font-semibold">Account Number</Label><Input data-testid="profile-bank-account" value={form.bank_account || ""} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} className="mt-1.5 h-11 rounded-lg" /></div>
            <div><Label className="font-semibold">IFSC</Label><Input data-testid="profile-ifsc" value={form.ifsc || ""} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} className="mt-1.5 h-11 rounded-lg uppercase" /></div>
            <div><Label className="font-semibold">Referral Code (yours)</Label><Input value={form.referral_code || ""} disabled className="mt-1.5 h-11 rounded-lg bg-stone-100 font-mono" /></div>
          </div>
        </div>

        <div className="pt-4 border-t border-stone-200">
          <h3 className="font-display text-lg font-black mb-1">KYC Document uploads</h3>
          <p className="text-xs text-stone-500 mb-4">JPG / PNG / WebP, &le; 2 MB each. Auto-resized client-side. All four are needed before operations can verify your profile.</p>
          <KycDocsSection form={form} onChange={(next) => setForm(next)} locked={profileLocked} />
        </div>

        <Button type="submit" data-testid="profile-save-button" disabled={loading} className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold px-7 h-12">
          {loading ? "Saving..." : "Save Profile"}
        </Button>
      </form>
    </div>
  );
}
